import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { COMPANY } from "@/lib/siteData";

const PlumberScene = () => (
  <svg viewBox="0 0 440 300" className="pipe-scene w-full h-auto" role="img" aria-label="Cartoon plumber fixing a leaking pipe">
    <defs>
      <linearGradient id="pipeGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#e2e8f0" />
        <stop offset="0.45" stopColor="#94a3b8" />
        <stop offset="1" stopColor="#475569" />
      </linearGradient>
      <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#eaf4ff" />
        <stop offset="1" stopColor="#ffffff" />
      </linearGradient>
    </defs>

    {/* backdrop */}
    <rect x="0" y="0" width="440" height="300" rx="18" fill="url(#skyGrad)" />
    {/* retro brick floor */}
    <g>
      <rect x="0" y="262" width="440" height="38" fill="#e8b06a" />
      <rect x="0" y="262" width="440" height="38" fill="none" />
      {[0, 60, 120, 180, 240, 300, 360, 420].map((x) => (
        <line key={x} x1={x} y1="262" x2={x} y2="281" stroke="#c98a3f" strokeWidth="2" />
      ))}
      {[30, 90, 150, 210, 270, 330, 390].map((x) => (
        <line key={"b" + x} x1={x} y1="281" x2={x} y2="300" stroke="#c98a3f" strokeWidth="2" />
      ))}
      <line x1="0" y1="281" x2="440" y2="281" stroke="#c98a3f" strokeWidth="2" />
    </g>

    {/* puddle */}
    <ellipse className="puddle" cx="150" cy="268" rx="62" ry="9" fill="#57b6ff" opacity="0.75" />

    {/* pipe */}
    <g>
      <rect x="18" y="150" width="252" height="32" rx="9" fill="url(#pipeGrad)" />
      <rect x="14" y="145" width="18" height="42" rx="5" fill="#334155" />
      <rect x="150" y="140" width="36" height="52" rx="7" fill="#64748b" stroke="#334155" strokeWidth="3" />
      <circle cx="160" cy="152" r="2.4" fill="#334155" />
      <circle cx="176" cy="152" r="2.4" fill="#334155" />
      <rect x="256" y="150" width="32" height="32" rx="9" fill="url(#pipeGrad)" />
      <rect x="245" y="182" width="20" height="44" rx="7" fill="url(#pipeGrad)" />
    </g>

    {/* leak drips */}
    <g>
      <ellipse className="drip" cx="167" cy="196" rx="5" ry="7" fill="#2f9bf0" />
      <ellipse className="drip d2" cx="157" cy="196" rx="4" ry="6" fill="#2f9bf0" />
      <ellipse className="drip d3" cx="177" cy="196" rx="4" ry="6" fill="#2f9bf0" />
    </g>

    {/* sparkle at fix point */}
    <g className="spark">
      <path d="M188 126 l3.5 9 9 3.5 -9 3.5 -3.5 9 -3.5 -9 -9 -3.5 9 -3.5 z" fill="#FFB300" />
    </g>

    {/* ===== MARIO-STYLE PLUMBER ===== */}
    <g className="plumber">
      {/* boots */}
      <path d="M305 258 q-2 12 12 12 h10 v-16 h-22 z" fill="#5b3a1a" />
      <path d="M345 258 q-2 12 12 12 h12 v-16 h-24 z" fill="#5b3a1a" />
      {/* legs (blue overalls) */}
      <rect x="308" y="224" width="18" height="36" rx="7" fill="#2551C6" />
      <rect x="344" y="224" width="18" height="36" rx="7" fill="#2551C6" />
      {/* red shirt torso */}
      <rect x="304" y="170" width="62" height="58" rx="18" fill="#e11d0e" />
      {/* overall front (blue) */}
      <path d="M316 196 h38 v30 q-19 10 -38 0 z" fill="#2551C6" />
      {/* straps */}
      <rect x="320" y="176" width="8" height="26" rx="4" fill="#2551C6" />
      <rect x="342" y="176" width="8" height="26" rx="4" fill="#2551C6" />
      {/* gold buttons */}
      <circle cx="324" cy="204" r="4.2" fill="#FFC93C" stroke="#e69a00" strokeWidth="1.5" />
      <circle cx="346" cy="204" r="4.2" fill="#FFC93C" stroke="#e69a00" strokeWidth="1.5" />

      {/* left glove resting on pipe elbow */}
      <circle cx="300" cy="196" r="9" fill="#ffffff" stroke="#d1d5db" strokeWidth="1.5" />

      {/* head */}
      <circle cx="335" cy="150" r="22" fill="#F4C79B" />
      {/* ear */}
      <circle cx="358" cy="152" r="4.5" fill="#e8b787" />
      {/* big nose */}
      <circle cx="320" cy="154" r="6.5" fill="#F0B98A" />
      {/* eyes */}
      <ellipse cx="330" cy="145" rx="3" ry="4" fill="#1f2937" />
      <ellipse cx="342" cy="145" rx="3" ry="4" fill="#1f2937" />
      {/* mustache */}
      <path d="M314 160 q10 10 22 6 q12 4 22 -6 q-8 12 -22 8 q-14 4 -22 -8 z" fill="#4a2c14" />
      {/* cap */}
      <path d="M312 142 a23 23 0 0 1 46 -2 l-46 5 z" fill="#e11d0e" />
      <path d="M308 145 q-8 2 -14 8 q10 -2 18 -2 z" fill="#c81a0d" />
      <rect x="311" y="140" width="48" height="7" rx="3.5" fill="#c81a0d" />
      {/* cap emblem */}
      <circle cx="335" cy="128" r="8" fill="#ffffff" />
      <text x="335" y="132" textAnchor="middle" fontSize="10" fontWeight="800" fill="#2551C6" fontFamily="Outfit, sans-serif">A</text>

      {/* right arm + wrench (pumping to tighten) */}
      <g className="wrench-arm">
        {/* upper arm */}
        <path d="M310 194 q-38 -4 -74 -12" stroke="#e11d0e" strokeWidth="16" fill="none" strokeLinecap="round" />
        {/* white glove hand */}
        <circle cx="234" cy="180" r="10" fill="#ffffff" stroke="#d1d5db" strokeWidth="1.5" />
        {/* wrench */}
        <g transform="rotate(-28 205 174)">
          <rect x="172" y="169" width="48" height="12" rx="6" fill="#3b82f6" />
          <path d="M166 162 a14 14 0 1 0 0 24 l7 -6.5 a7.5 7.5 0 1 1 0 -11 z" fill="#2563eb" />
        </g>
      </g>
    </g>
  </svg>
);

export const LeakFix = () => {
  const go = () => document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="leak-fix" className="py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center rounded-[2.5rem] border-2 border-[#1E3A8A] bg-[#FFF8E1] p-8 md:p-14">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-4">Leak emergency?</p>
            <h2 className="font-display font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl text-[#2551C6]">
              Got a leak?<br /><span className="text-[#FF3B30]">We're on it.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-neutral-700 max-w-md">
              Dripping joints, burst pipes, mystery puddles — we track down the source and fix it fast, so a small leak never becomes big damage.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="leak-quote-btn"
                onClick={go}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2551C6] text-white font-bold px-7 py-3.5 hover:bg-[#1E3A8A] transition-colors duration-300"
              >
                Fix My Leak
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
              <a
                data-testid="leak-call-btn"
                href={COMPANY.phoneHref}
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#FF3B30] text-[#FF3B30] font-bold px-7 py-3.5 hover:bg-[#FF3B30] hover:text-white transition-colors duration-300"
              >
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-white ring-1 ring-black/10 p-4 md:p-6"
          >
            <PlumberScene />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
